import { Disclosure } from '@headlessui/react';
import { useCallback } from 'react';

import { CaretUpIcon } from '~components/icons';
import { CaretDownIcon } from '~components/icons/CaretDownIcon';
import useAssets from '~hooks/useAssets';
import { ShareFormatListItemPanelController } from '~modules/media-library/controller/ShareFormatListItemPanelController';
import { AssetId } from '~modules/media-library/model/Asset';
import { DownloadFormat } from '~modules/media-library/model/DownloadFormat';
import { AddToMyfilesButton } from '~modules/media-library/view/AddToMyfilesButton';
import { ShareAction } from '~modules/media-library/view/ShareModal/ShareAction';
import { shareFileMam } from '~services/api/calls/file';

type Props = {
  assetId: AssetId;
  downloadFormat: DownloadFormat;
  disabled: boolean;
  shareAction: ShareAction;
};

export function ShareFormatListItem({ assetId, downloadFormat, disabled, shareAction }: Props) {
  const { getAssetById } = useAssets();

  // Handler: add to myfiles with format
  const handleOnAddToMyfiles = useCallback(
    async (id: AssetId) => {
      const asset = getAssetById(id);
      if (!asset) return;
      return await shareFileMam(id, asset.type, downloadFormat.key);
    },
    [downloadFormat, getAssetById],
  );

  // early exit
  if (!downloadFormat) return null;

  if (shareAction === ShareAction.AddToMyfiles) {
    return (
      <Disclosure>
        <Disclosure.Button
          className={`appearance-none flex w-full justify-between items-center border-none outline-none text-left rounded-md transition shadow-none bg-white`}
          disabled={disabled}
        >
          <span className="flex flex-col p-2">
            <span className="font-sans font-bold text-base">{downloadFormat.label || downloadFormat.key}</span>
            <span className="font-sans text-sm">{downloadFormat.description}</span>
          </span>
          <div className={`w-8 h-8 pr-3 ${disabled ? 'hidden' : null}`}>
            <AddToMyfilesButton assetId={assetId} onAddToMyfiles={handleOnAddToMyfiles} />
          </div>
        </Disclosure.Button>
      </Disclosure>
    );
  }

  return (
    <Disclosure>
      {({ open }) => (
        <>
          <Disclosure.Button
            className={`appearance-none flex w-full justify-between items-center border-none outline-none text-left rounded-md transition shadow-none hover:shadow-md cursor-pointer ${
              open ? 'bg-gray' : 'bg-white hover:bg-gray'
            }`}
            disabled={disabled}
          >
            <span className="flex flex-col p-2">
              <span className="font-sans font-bold text-base">{downloadFormat.label || downloadFormat.key}</span>
              <span className="font-sans text-sm">{downloadFormat.description}</span>
            </span>
            <div className="w-8 h-8 p-1">{open ? <CaretUpIcon /> : <CaretDownIcon />}</div>
          </Disclosure.Button>
          <Disclosure.Panel className="px-4 py-2">
            <ShareFormatListItemPanelController
              assetId={assetId}
              downloadFormat={downloadFormat}
              shareAction={shareAction}
            />
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}
